import DOMPurify from "dompurify";

/**
 * Wire <-> UI mapping for progress notes. Everything here is driven by the REAL
 * dev1 payload, e.g.
 *   { _id, patientId: 19727, tag: 1, body: "AppointmentId: 17410 <br />…",
 *     author: { name: "", employeeId: 0 }, acknowledgers: [], comments: [],
 *     _created: "17-07-2026 10:25:36 AM" }
 */

/**
 * Tags. The wire carries an int; the chips show the name. Values are bridge's
 * constants/type/progress-notes/ProgressNoteTag.js — ALL(0) is the "no filter"
 * pseudo-tag and is never a note's own tag.
 */
export const TAGS = [
  { name: "ALL", value: 0 },
  { name: "CALL_UPDATE", value: 1 },
  { name: "MEDICAL_RECORDS", value: 2 },
  { name: "NUTRITION", value: 3 },
  { name: "CM_CHAT", value: 4 },
  { name: "MIN", value: 5 },
  { name: "BD", value: 6 },
  { name: "ESCALATION", value: 7 },
  { name: "PHYSIOTHERAPY", value: 8 },
  { name: "DOCTOR_NOTES", value: 9 },
];

export const TAG_ALL = 0;

const BY_VALUE = TAGS.reduce((m, t) => ({ ...m, [t.value]: t.name }), {});

/** int -> chip name. Unknown/absent tags fall back to the neutral label. */
export const tagName = (value) => BY_VALUE[value] || "CALL_UPDATE";

/** chip name -> int. */
export const tagValue = (name) => {
  const t = TAGS.find((x) => x.name === name);
  return t ? t.value : TAG_ALL;
};

/**
 * `_created` is NOT ISO — the API emits "DD-MM-YYYY hh:mm:ss AM/PM" (12-hour,
 * no zone), so Date.parse cannot read it and would silently yield Invalid Date.
 *
 * AND THE DIGITS ARE UTC. Eve stores _created as a UTC instant and renders it
 * through a strftime pattern that drops the zone marker, so the string itself
 * never says so. Two shipped consumers of this exact field prove it, both by
 * adding 5h30m before they display anything:
 *
 *   bridge's own notes screen — ops/modules/patients/modules/new-bridge/
 *     components/ProgressNotes.js:1110  `.getTime() + 5.5 * 60 * 60 * 1000`
 *   care-manager-frontend — components/home/home-third-panel/
 *     progress-notes-container/pn-container.component.ts:126
 *     `.add(30, "minutes").add(5, "hours")`
 *
 * This port copied bridge's hand-parse and dropped the offset with it, so every
 * stamp read 5h30m early — a note written at 4:20 PM showed as 10:50 AM.
 *
 * Date.UTC, so what comes back is the REAL instant rather than a fictional
 * local one. formatCreated renders it in IST; anything that later sorts or
 * compares these gets something that means what it says.
 */
export const parseCreated = (created) => {
  if (!created || typeof created !== "string") return null;
  const parts = created.trim().split(" ");
  if (parts.length < 2) return null;

  const [dd, mm, yyyy] = parts[0].split("-").map((n) => parseInt(n, 10));
  const [rawH, min, sec] = parts[1].split(":").map((n) => parseInt(n, 10));
  if ([dd, mm, yyyy, rawH].some((n) => isNaN(n))) return null;

  let hour = rawH % 12;
  if ((parts[2] || "").toUpperCase() === "PM") hour += 12;

  const d = new Date(Date.UTC(yyyy, mm - 1, dd, hour, min || 0, sec || 0));
  return isNaN(d.getTime()) ? null : d;
};

/**
 * IST, PINNED — not the viewer's own zone.
 *
 * These stamps are a clinical audit trail. Two people looking at the same note
 * have to read the same time off it, and every reader of this app is in one
 * place; a viewer-local stamp would have a colleague abroad quoting a different
 * time for the same event. Asia/Kolkata rather than the hardcoded +5:30 the two
 * older consumers use: identical today, since IST has no DST, but it says what
 * it means instead of encoding an offset.
 *
 * formatToParts, not format(), for two reasons: the assembled string is the
 * board's own layout, and recent ICU puts a NARROW NO-BREAK SPACE before AM/PM
 * in a formatted string — invisible in a diff, and it would not match a test.
 */
const IST_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Kolkata",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/** The card's timestamp: "11:11 AM · 06 Jul 2026" (Figma 2155:3258). */
export const formatCreated = (created) => {
  const d = parseCreated(created);
  if (!d) return "";

  const p = {};
  IST_PARTS.formatToParts(d).forEach(({ type, value }) => {
    p[type] = value;
  });

  return `${p.hour}:${p.minute} ${(p.dayPeriod || "").toUpperCase()} · ${p.day} ${p.month} ${p.year}`;
};

/** Initials for the author avatar. "Zyla Health" -> "ZH"; empty -> "?". */
export const initialsOf = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return (words[0][0] + (words[1] ? words[1][0] : "")).toUpperCase();
};

/* ---------------------------------------------------------------------------
 * Body HTML.
 *
 * Note bodies contain markup — the real payload has "<br />", and the composer's
 * rich-text toolbar emits bold/italic/lists/links — so they cannot render as
 * plain text. Bridge pipes them straight into dangerouslySetInnerHTML via
 * `createMarkup = s => ({__html: s})`: no escaping, no sanitizer. That makes any
 * stored note a script-injection vector for every viewer, so phr deliberately
 * does NOT copy it. We render the markup but sanitize it with DOMPurify first.
 * ------------------------------------------------------------------------- */

/**
 * Formatting the composer's toolbar and the server's own notes actually emit.
 * Headings (h1–h3) back the block-style dropdown; `div`/`p` are the block wrappers
 * a contentEditable produces on Enter, so keeping them preserves line breaks.
 * All are structure-only — none is a script vector, and only `a` carries attrs.
/* WHAT THE EDITOR CAN EMIT, and therefore what survives a save. The composer's
   toolbar was cut down to what this list kept — size, font, colour, alignment
   and images were dropped BECAUSE they were stripped here — so the list is what
   actually decides the feature, and it is widened deliberately to match
   legacy's editor (Nittin, 2026-09-24).

   Each entry is something document.execCommand really produces, measured in
   Chrome rather than assumed:
     superscript/subscript                      -> <sup>/<sub>
     fontSize/fontName/foreColor, styleWithCSS  -> <span style="…">
     justify*                                   -> <div style="text-align: …">
     indent                                     -> <blockquote style="margin: …">
     a code span -> <code>, an image -> <img src>

   THE COMMANDS RUN WITH styleWithCSS ON for size, font and colour, which is the
   opposite of the rest of the toolbar, and the reason is DOMPurify: with it OFF
   they emit <font size|face|color>, and DOMPurify 3.4 strips those attributes
   even when ALLOWED_ATTR names them (measured). A <span style> survives. */
const ALLOWED_TAGS = [
  "br", "b", "strong", "i", "em", "u", "s", "strike", "p", "div", "span",
  "h1", "h2", "h3", "ul", "ol", "li", "a",
  "sup", "sub", "code", "pre", "blockquote", "img", "iframe",
];

/* THE ONLY HOSTS A NOTE MAY FRAME. Legacy's toolbar has an "embedded" button
   because react-draft-wysiwyg's default toolbar does, and it frames whatever
   URL it is given. An iframe in a clinical note is the one thing here with a
   real blast radius — it is rendered by phr, by bridge (which does not
   sanitize on the way out) and by the patient app — so the composer offers
   only video hosts and this drops everything else, including anything pasted
   into a body by hand. */
const EMBED_HOSTS = /^(?:www\.)?(?:youtube\.com|youtu\.be|player\.vimeo\.com|vimeo\.com)$/i;

/* THE CSS A NOTE MAY CARRY. `style` has to be allowed for alignment and indent
   — there is no attribute form of either — and DOMPurify does NOT filter style
   values (measured: `behavior:url(x)` came through untouched). So the
   declarations are allow-listed here and everything else is dropped in
   scrubMarkup below, rather than trusting the parser to be a filter it is not. */
const ALLOWED_CSS = [
  "text-align", "margin", "margin-left", "color", "background-color",
  "font-size", "font-family", "font-weight", "font-style", "text-decoration",
];

/**
 * The second pass, over DOMPurify's own output.
 *
 * TWO THINGS DOMPURIFY WILL NOT DO FOR US, both measured rather than assumed:
 *   · it does not filter `style` declarations at all;
 *   · it allows `data:` URIs on <img> whatever ALLOWED_URI_REGEXP says, because
 *     img is one of its DATA_URI_TAGS — so a note body would be a place to
 *     smuggle an arbitrary payload past the allow-list.
 * Both are closed here, on the DOM rather than with a regex.
 */
const scrubMarkup = (html) => {
  if (!html || !/style=|<img|<iframe/i.test(html)) return html;
  const root = document.createElement("div");
  // Safe to assign: this only ever runs on markup DOMPurify has already cleaned.
  root.innerHTML = html;

  root.querySelectorAll("[style]").forEach((el) => {
    const keep = [];
    for (let i = 0; i < el.style.length; i += 1) {
      const prop = el.style.item(i);
      if (ALLOWED_CSS.indexOf(prop) !== -1) {
        keep.push(`${prop}: ${el.style.getPropertyValue(prop)}`);
      }
    }
    if (keep.length) el.setAttribute("style", keep.join("; "));
    else el.removeAttribute("style");
  });

  root.querySelectorAll("img").forEach((el) => {
    if (!/^https?:/i.test(el.getAttribute("src") || "")) el.parentNode.removeChild(el);
  });

  root.querySelectorAll("iframe").forEach((el) => {
    let host = "";
    try {
      host = new URL(el.getAttribute("src") || "", window.location.origin).hostname;
    } catch (e) {
      host = "";
    }
    if (!EMBED_HOSTS.test(host)) el.parentNode.removeChild(el);
  });

  return root.innerHTML;
};

/**
 * Turn a wire body into markup that is safe to inject. DOMPurify parses the
 * markup properly rather than pattern-matching it, which is the whole point:
 * hand-rolled regex sanitisers miss mutation-XSS and namespace tricks that a
 * real parser catches.
 *
 * `a` is allowed but only carries href/target/rel, and ALLOWED_URI_REGEXP pins
 * the scheme to http/https/mailto — so `javascript:`/`data:` hrefs are dropped.
 */
export const sanitizeBody = (body) => {
  if (!body) return "";
  return scrubMarkup(
    DOMPurify.sanitize(String(body), {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ["href", "target", "rel", "style", "src", "alt", "title"],
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
      // Keep the text of anything dropped (a <script>'s contents are still
      // removed — DOMPurify drops those wholesale), so removing a stray tag
      // never silently eats a clinician's words.
      KEEP_CONTENT: true,
    })
  );
};

/**
 * Plain text typed by a clinician -> a body the wire will accept.
 *
 * Bodies are STORED as HTML and rendered as HTML by every reader, but phr's
 * composers are plain textareas — so what was typed is text and must be escaped
 * before it becomes markup. Two reasons, and the second is the important one:
 *   1. a typed "<b>" would otherwise silently turn into formatting;
 *   2. phr sanitizes on READ (sanitizeBody), but BRIDGE renders these same notes
 *      with no sanitizer at all. Writing unescaped input would let phr author a
 *      stored payload that bridge executes. We must not be the injection vector
 *      for another app.
 * Newlines then become <br>, or the note renders as one blob.
 *
 * Only ever feed this USER text. Markup we generate ourselves (the task list)
 * must be appended AFTER, never passed through here.
 */
export const toWireBody = (text) =>
  String(text == null ? "" : text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\n/g, "<br>");

/**
 * A comment, display-ready. Comments are note-shaped — `{_id, body, author,
 * acknowledgers, _created}` — so the body is HTML and the date is the same
 * non-ISO string, and they carry their OWN acknowledgers (a comment can be
 * liked independently of its note).
 */
export const toComment = (c) => ({
  id: c._id,
  html: sanitizeBody(c.body),
  author: (c.author && c.author.name) || "Zyla Health",
  initials: initialsOf((c.author && c.author.name) || "Zyla Health"),
  time: formatCreated(c._created),
  acks: (c.acknowledgers || []).length,
  acknowledgers: c.acknowledgers || [],
});

/** What ProgressNotes renders. Keeps the wire note but adds display-ready bits. */
export const toCard = (note) => ({
  id: note._id,
  category: tagName(note.tag),
  html: sanitizeBody(note.body),
  author: (note.author && note.author.name) || "Zyla Health",
  initials: initialsOf((note.author && note.author.name) || "Zyla Health"),
  time: formatCreated(note._created),
  // The two footer metrics: replies are the embedded comments, the thumb is an
  // acknowledgement. Both arrays are always present on the wire.
  reactions: [
    (note.comments || []).length,
    (note.acknowledgers || []).length,
  ],
  // Newest-first, like bridge (it reverses the embedded array, which the server
  // returns oldest-first).
  comments: (note.comments || []).map(toComment).reverse(),
  acknowledgers: note.acknowledgers || [],
});

/**
 * WHO IS WRITING — the `{ name, employeeId }` an author, acknowledger or
 * comment must carry.
 *
 * THE DOCTOR ID GOES IN `employeeId`. A DOCTOR SESSION HAS NO employeeProfile
 * — in either repo. phr signs in through /doclogin, where `doctor_login.go`
 * sets employeeProfile to null unconditionally (phr's
 * scripts/verify/doctor-login.js asserts that shape), and bridge's own
 * `isDoctorLogin()` is literally `doctorProfile.id > 0 && !employeeProfile`
 * (its modules/user/service/UserService.js). So reading employeeProfile.id gave
 * `undefined` and the server answered 422
 * `{"author": {"employeeId": "required field"}}` on every create, comment and
 * acknowledge. The name went out blank for the same reason.
 *
 * `employeeId` is a misnomer on this wire, not a type: the expert dashboard has
 * always put the doctor id there for a doctor session — bridge's
 * ops/.../new-bridge/components/ProgressNotes.js assigns doctorProfile to its
 * `employeeProfile` variable and then stamps `employeeProfile.id` — so the
 * notes already in Mongo carry doctor ids, and matching that is what keeps
 * `hasAcknowledged` below and the feed's "mine" checks working against existing
 * data.
 *
 * Employee first, then doctor, so a care-manager login is unaffected. The
 * profile's TOP-LEVEL `id` is deliberately not in the chain: that is the
 * platform auth id (rxMappers' `myUid`, what a prescription's createdBy
 * records), a different namespace, and writing it here would stamp an actor
 * neither dashboard looks for.
 */
export const noteAuthor = (userProfile) => {
  const u = userProfile || {};
  const emp = u.employeeProfile || {};
  const doc = u.doctorProfile || {};
  const empName = [emp.firstName, emp.lastName]
    .filter(Boolean)
    .join(" ")
    /* Collapse rather than trim the ends — a stored firstName of "Tara " went
       out as "Tara  Emp". */
    .replace(/\s+/g, " ")
    .trim();
  return {
    name: empName || doc.name || "",
    employeeId: emp.id || doc.id || 0,
  };
};

/**
 * Has this employee already acknowledged? The server rejects a duplicate with
 * "You have already acknowledged it.", so the UI should not offer the action
 * twice.
 */
export const hasAcknowledged = (acknowledgers, employeeId) =>
  (acknowledgers || []).some((a) => a && a.employeeId === employeeId);
